import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Copy, Trash2, Upload } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { uploadImage, useMedia } from '../fields/MediaField'
import { Empty, Notice, PageHeader, Spinner, ago } from '../components/ui'

const FOLDERS = ['misc', 'partners', 'team'] as const

export default function MediaPage() {
  const media = useMedia()
  const qc = useQueryClient()
  const { user } = useAuth()
  const input = useRef<HTMLInputElement>(null)
  const [folder, setFolder] = useState<(typeof FOLDERS)[number]>('misc')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function upload(files: FileList) {
    setBusy(true)
    setError(null)
    try {
      for (const file of Array.from(files)) await uploadImage(file, folder)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
      await qc.invalidateQueries({ queryKey: ['media'] })
    }
  }

  async function remove(key: string) {
    if (!window.confirm(`Delete ${key}?`)) return
    try {
      await api.del(`/media/file/${key}`)
      await qc.invalidateQueries({ queryKey: ['media'] })
    } catch (err) {
      setError((err as Error).message)
    }
  }

  return (
    <>
      <PageHeader title="Media">
        <select className="input !w-auto" value={folder} onChange={(e) => setFolder(e.target.value as typeof folder)} aria-label="Folder">
          {FOLDERS.map((f) => <option key={f}>{f}</option>)}
        </select>
        <button className="btn-primary" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? <Spinner /> : <Upload className="h-3.5 w-3.5" />} Upload
        </button>
        <input ref={input} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden"
               onChange={(e) => { if (e.target.files?.length) void upload(e.target.files); e.target.value = '' }} />
      </PageHeader>
      <p className="mb-4 text-xs text-faint">Images are resized to at most 1600px and stored as WebP. Each item's image field can also upload directly.</p>
      {error && <div className="mb-4"><Notice>{error}</Notice></div>}
      {media.isPending && <Spinner />}
      {media.data && media.data.items.length === 0 && <Empty>No images yet.</Empty>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {media.data?.items.map((m) => {
          const url = `${media.data.publicBaseUrl}/${m.key}`
          return (
            <figure key={m.key} className="card overflow-hidden">
              <div className="aspect-video bg-surface-2"><img src={url} alt="" className="h-full w-full object-contain" loading="lazy" /></div>
              <figcaption className="space-y-1 p-2">
                <p className="truncate font-mono text-[11px]" title={m.key}>{m.key}</p>
                <p className="text-[11px] text-faint">{Math.round(m.bytes / 1024)} KB · {ago(m.uploaded_at)}</p>
                <div className="flex gap-1">
                  <button className="btn !px-2 !py-0.5 text-xs" onClick={() => void navigator.clipboard.writeText(url)}>
                    <Copy className="h-3 w-3" /> URL
                  </button>
                  {user.role === 'admin' && (
                    <button className="btn !px-2 !py-0.5 text-xs text-danger" onClick={() => void remove(m.key)}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </figcaption>
            </figure>
          )
        })}
      </div>
    </>
  )
}
